<template>
  <footer :class="$style.footer" data-app-footer-v="3.1.2">
    <div :class="$style.inner">
      <!-- Nav Group -->
      <div
        v-for="navGroup in data?.siteFooter?.fields?.navigationGroups?.filter(
          instanceOfNavigationGroup,
        )"
        :key="navGroup?.sys?.id"
        :class="$style.navGroup"
      >
        <h6 :class="$style.groupLabel">{{ navGroup?.fields?.groupLabel }}</h6>
        <ul :class="$style.navList">
          <li
            v-for="linkItem in navGroup?.fields?.navigationLinks?.filter(
              instanceOfLinkable,
            )"
            :key="linkItem?.sys?.id"
          >
            <NuxtLinkLocale
              :to="linkItem?.fields?.link || ''"
              :target="linkItem?.fields?.target"
              :class="$style.link"
              @click="(e: MouseEvent) => sendToGa(e, linkItem)"
            >
              {{ linkItem?.fields?.label }}
            </NuxtLinkLocale>
          </li>
        </ul>
      </div>

      <!-- Copyright Block -->
      <div :class="$style.copyrightContainer">
        <div :class="$style.copyrightTop">
          <NuxtLinkLocale
            v-if="instanceOfLinkable(computedMainLogo)"
            :to="computedMainLogo?.fields?.link"
            :target="computedMainLogo?.fields?.target"
          >
            <BasePicture
              :img-class="$style.mainLogo"
              :src="`${computedMainLogo?.fields?.media?.fields?.file?.url}?w=197`"
              :alt="computedMainLogo?.fields?.label || ''"
              width="197"
              height="136"
            />
          </NuxtLinkLocale>

          <p :class="$style.copyright">{{ computedCopyright }}</p>
        </div>

        <div v-if="computedLogos?.length" :class="$style.copyrightBottom">
          <template v-for="logo in computedLogos">
            <NuxtLinkLocale
              v-if="instanceOfLinkable(logo)"
              :key="logo?.sys?.id"
              :to="logo?.fields?.link"
              :target="logo?.fields?.target"
            >
              <AspectRatio
                :class="$style.logo"
                :ratio="`${logo?.fields?.media?.fields?.file?.details?.image?.width}x${logo?.fields?.media?.fields?.file?.details?.image?.height}`"
              >
                <BasePicture
                  :img-class="$style.logo"
                  :src="`${logo?.fields?.media?.fields?.file?.url}?w=65&h=86`"
                  :alt="logo?.fields?.media?.fields?.title || ''"
                />
              </AspectRatio>
            </NuxtLinkLocale>
          </template>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import {
  instanceOfLinkable,
  instanceOfNavigationGroup,
} from '~/utils/instanceOfEntry'

import type {
  TypeTheSiteFooterSkeleton,
  TypeLinkablesWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { $client } = useNuxtApp()
const { locale } = useI18n()
const cookieStore = useCookieStore()

const { data } = await useAsyncData(
  `site-footer-${cookieStore.languageCookie}-${cookieStore.countryCookie}`,
  async () => {
    const payload =
      await $client.withoutUnresolvableLinks.getEntries<TypeTheSiteFooterSkeleton>(
        {
          content_type: 'theSiteFooter',
          limit: 1,
          include: 10,
          locale: locale.value,
        },
      )

    return payload?.items?.[0]
  },
  {
    transform(input) {
      return {
        siteFooter: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

const computedCopyright = computed(() => {
  if (!data.value?.siteFooter?.fields?.copyright) return ''
  const currentYear = new Date().getFullYear().toString()
  return data.value?.siteFooter?.fields?.copyright?.replace(
    '{{YEAR}}',
    currentYear,
  )
})

const computedLogos = computed(() =>
  data.value?.siteFooter?.fields?.logos?.filter(instanceOfLinkable),
)

const computedMainLogo = computed(
  () => data.value?.siteFooter?.fields?.mainLogo,
)

const specialCaseHandlers: Record<string, () => void> = {
  /**
   * Handle the special case when the cta identifier is 'ketch:showPreferences'
   */
  onRequestToShowKetchPreferences: () => {
    if (typeof window.ketch === 'function') {
      window.ketch('showPreferences', {
        showRightsTab: true,
      })
    }
  },
}

const sendToGa = (
  e: Event,
  linkable: TypeLinkablesWithoutUnresolvableLinksResponse,
) => {
  if (linkable?.fields?.identifier) {
    const SPECIAL_CASES = [
      {
        identifier: 'ketch::showPreferences',
        handler: 'onRequestToShowKetchPreferences',
      },
    ]
    const specialCase = SPECIAL_CASES.find(
      (sc) => sc.identifier === linkable?.fields?.identifier,
    )
    if (specialCase) {
      e.preventDefault()
      if (typeof specialCaseHandlers[specialCase.handler] === 'function') {
        specialCaseHandlers[specialCase.handler]()
      } else {
        console.warn('Special case handler not found:', specialCase.handler)
      }
    }
  }

  if (!linkable?.fields?.label || !linkable?.fields?.link) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'ga4.trackEvent',
    eventName: 'menu_navigation',
    eventParams: {
      menu_type: 'footer',
      link_text: linkable?.fields?.label,
      link_url: linkable?.fields?.link,
    },
  })
}
</script>

<style lang="scss" module>
.footer {
  background-color: var(--black);
  color: var(--white);
}

.inner {
  max-width: 1250px;
  margin: auto;
  padding: 50px 25px;
  display: grid;
  grid-auto-flow: row;
  gap: 35px;

  @include media('md') {
    grid-auto-flow: column;
    padding-bottom: 85px;
  }
}

.groupLabel {
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  margin-bottom: 10px;
}

.navList {
  @include list-unstyled();
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;

  @include media('xs') {
    grid-template-columns: repeat(3, 1fr);
  }

  @include media('md') {
    grid-template-columns: 1fr;
  }
}

.groupLabel {
  font-size: to-rem(17px);
  line-height: math.div(41px, 17px);
}

.link {
  font-size: to-rem(15px);
  color: var(--white);
  opacity: 0.6;
  text-decoration: none;

  @include media('md') {
    font-size: to-rem(17px);
    line-height: math.div(41px, 17px);
  }
}

.copyrightContainer {
  display: grid;
  justify-content: center;
  gap: 25px;

  @include media('xxs') {
    grid-template-columns: 190px 1fr;
  }

  @include media('md') {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.copyrightTop {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 217px;
  gap: 5px;

  @include media('xxs') {
    align-items: flex-start;
  }

  @include media('md') {
    align-items: flex-end;
  }
}

.mainLogo {
  max-width: 197px;
  margin: auto auto 8px;

  @include media('xxs') {
    margin-left: 0;
  }

  @include media('md') {
    margin-left: auto;
    margin-right: 0;
  }
}

.copyright {
  font-size: to-rem(12px);
  line-height: math.div(18px, 12px);
  opacity: 0.6;
  text-align: center;

  @include media('xxs') {
    text-align: left;
  }

  @include media('md') {
    text-align: right;
  }
}

.copyrightBottom {
  display: grid;
  justify-content: center;
  align-items: center;
  grid-auto-flow: column;
  gap: 20px;
  grid-template-columns: repeat(2, minmax(auto, 65px));

  @include media('xxs') {
    justify-content: initial;
  }
}

.logo {
  max-width: 65px;
  width: 100%;
}
</style>
